import type { GetStaticPropsContext } from 'next';
import { pick } from 'lodash-es';
import { z } from 'zod';

import { Heading } from '@chakra-ui/react';

import { FAQSection } from '~/components/faq-section';
import { PageCategory } from '~/features/analytics/constants';
import { getCatalogItemsFromServer } from '~/features/catalog/controllers/get-catalog-items-from-server';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { ContentSection } from '~/features/seo/components/content-section';
import { FeaturedSchoolSection } from '~/features/seo/components/featured-school-section';
import { FeaturedVideoSection } from '~/features/seo/components/featured-video-section';
import { FooterBannerSection } from '~/features/seo/components/footer-banner-section';
import { ProgramListSection } from '~/features/seo/components/program-list-section';
import { WaveHeroSection } from '~/features/seo/components/wave-hero-section';
import { getAllTopicContents } from '~/features/seo/controllers/get-all-topic-contents';
import { getTopicContent } from '~/features/seo/controllers/get-topic-content';
import { Topic } from '~/features/seo/models/topic';

export default function TopicPage({
  topic,
  page,
  programs,
}: {
  topic: Topic;
  page: Page;
  programs: Array<Pick<CatalogItem, 'key' | 'image' | 'summary' | 'slug' | 'programDescriptionOverride' | 'title'>>;
}) {
  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Topic} />
      <PageLayout withoutVerticalPadding>
        <WaveHeroSection description={topic.pageSubtitle} title={topic.pageTitle} />
        {!!topic.featuredVideoYoutubeId && !!topic.featuredVideoTitle && (
          <FeaturedVideoSection
            description={topic.featuredVideoDescription}
            title={topic.featuredVideoTitle}
            youtubeId={topic.featuredVideoYoutubeId}
          />
        )}
        {!!topic.sections?.length && <ContentSection sections={topic.sections} />}
        {!!topic.featuredSchool && (
          <FeaturedSchoolSection
            ctaLabel={topic.featuredSchoolCtaLabel}
            description={topic.featuredSchoolDescription}
            school={topic.featuredSchool}
            title={topic.featuredSchoolTitle}
          />
        )}
        {!!programs?.length && <ProgramListSection programs={programs} title={topic.programsSectionTitle} />}
        {topic.footerBanner && (
          <FooterBannerSection
            ctaLabel={topic.footerBanner.ctaLabel}
            ctaLink={topic.footerBanner.ctaLink}
            subTitle={topic.footerBanner.subTitle}
            theme={topic.footerBanner.theme}
            title={topic.footerBanner.title}
          />
        )}
        {!!topic.faq?.length && (
          <FAQSection
            faq={topic.faq}
            titleSlot={
              <Heading
                as='h2'
                size='marketing-4xl'
                textAlign='center'
                marginBottom={{
                  base: 8,
                  md: 10,
                }}
              >
                Program FAQs
              </Heading>
            }
          />
        )}
      </PageLayout>
    </>
  );
}

export async function getStaticPaths() {
  const topics = await getAllTopicContents();
  return {
    paths: topics.map((topic) => ({ params: { slug: topic.slug } })),
    fallback: 'blocking',
  };
}

export async function getStaticProps({ locale, params }: GetStaticPropsContext) {
  const slugSchema = z.string().safeParse(params?.slug);
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE || !slugSchema.success) {
    return {
      notFound: true,
    };
  }

  const slug = slugSchema.data;

  const [page, topic] = await Promise.all([getPage('/topic'), getTopicContent(slug)]);

  if (!page || !topic) {
    return {
      notFound: true,
    };
  }

  const programs = getCatalogItemsFromServer(topic?.programs?.map((program) => program.key) || []);

  if (topic.seo) {
    page.seo = topic.seo;
    // Note: remove topic seo to reduce json size
    topic.seo = null;
  }

  return {
    props: {
      page,
      topic,
      programs: programs.map((program) =>
        pick(program, ['key', 'image', 'summary', 'slug', 'programDescriptionOverride', 'title'])
      ),
    },
  };
}
